import { all, takeLatest, call, put, select } from 'redux-saga/effects';
import get from 'lodash/get';
import find from 'lodash/find';
import omit from 'lodash/omit';
import isEmpty from 'lodash/isEmpty';
import makeQuery from 'yoda-core-components/lib/navigation/query/makeQuery';
import { addItemToCart } from 'yoda-interfaces/lib/Cart/CartApi';
import { fetchStores } from 'yoda-interfaces/lib/Common/LocationServiceApi';
import FactorySaga from 'yoda-site-components/lib/sagas/FactorySaga';
import { messagesActions } from 'yoda-site-components/lib/components/Messages';
import loadingActions from 'yoda-site-components/lib/actions/LoadingAction';
import {
    getSdpuStoreData,
    setSdpuLocalStorage,
} from 'yoda-site-components/lib/actions/SdpuStoreInfoAction';
import { X_CLIENT_SOURCE_PDP } from 'yoda-site-components/lib/common/Constants';
import { selectFeatureFlags } from 'yoda-site-components/lib/selectors/ContextSelector';
import { getDisableBYDeliveryDateFlag } from 'yoda-site-components/lib/selectors/byDeliveryDateSelector';
import selectProductType from 'yoda-site-components/lib/selectors/productDetails/selectProductType';
import {
    ADD_TO_CART_MESSAGES_ID,
    STORE_MODAL_ADD_TO_CART_MESSAGES_ID,
} from '../components/AddToCart/AddToCart.config';
import {
    ADDTOCART_POST_ERROR,
    ADDTOCART_POST_REQUEST,
    ADDTOCART_POST_SUCCESS,
} from '../actionTypes/AddToCartActionTypes';
import { formErrorAnalytics } from '../actions/AnalyticsAction';
import ClickStreamLogger from '../helpers/ClickStreamLogger';
import GetClickStreamLogData from '../helpers/PdpAtcClickStreamLogData';

const getGenericApiErrorMessages = (state) => state.context.messagesTexts.GenericApiErrorMessages;
const addCartClickPosition = (state) => state.addToCart.stickyHeaderOnClick;
let apiCounter = 0;

const getErrorMessage = ({ data = {} } = {}) => {
    const error = find(get(data, 'data', []), 'errors.length') || data;
    const { errorCode, errorMessage } = get(error, 'errors[0]', data);
    return { errorCode, errorMessage };
};
const getDeliveryOptions = ({ productOffering }) =>
    get(productOffering.promise, 'skuDeliveryOptions[0].deliveryOptions', {});

function* AddToCartSaga(action) {
    const { isStoreModalAtc = false, isChangeStoreAtc = false } = action && action.payload;
    const ADD_TO_CART_MSG_ID =
        isStoreModalAtc || isChangeStoreAtc
            ? STORE_MODAL_ADD_TO_CART_MESSAGES_ID
            : ADD_TO_CART_MESSAGES_ID;
    try {
        yield put(
            messagesActions.removeAllMessages({
                section: ADD_TO_CART_MSG_ID,
                sourceMiniPDP: false,
            })
        );
        const featureFlags = yield select(selectFeatureFlags);
        const { apiErrorMessage: defaultErrorMessage } = yield select(getGenericApiErrorMessages);
        const addItemAction = {};
        const sdpuStoreInfo = get(action, 'payload.sdpuStoreInfo', {});
        const deliveryOption = get(action, 'payload.deliveryOption', {});
        const disableBYDeliveryDate = yield select(getDisableBYDeliveryDateFlag);
        addItemAction.payload = {
            ...omit(action.payload, [
                'deliveryOption',
                'isChangeStoreAtc',
                'isStoreModalAtc',
                'sdpuStoreInfo',
                'location',
                'router',
            ]),
            xClientSource: !disableBYDeliveryDate ? X_CLIENT_SOURCE_PDP : null,
        };
        addItemAction.type = action.type;
        apiCounter++; // eslint-disable-line
        const cartSuccess = yield call(FactorySaga, addItemToCart, addItemAction);
        if (cartSuccess.isSuccess) {
            const productType = yield select(selectProductType()) || {};
            if (
                get(featureFlags, 'enableClickStreamLogs', true) &&
                productType.templateType !== 'bundle'
            ) {
                const deliveryOptionsOffering = yield select(getDeliveryOptions);
                const clickStreamLogData = GetClickStreamLogData(deliveryOptionsOffering, action);
                yield call(ClickStreamLogger, clickStreamLogData);
            }
            apiCounter = 0;
            cartSuccess.stickyHeaderOnClick = yield select(addCartClickPosition);
            const isBopisChecked =
                isStoreModalAtc ||
                (deliveryOption.sameDayPickUp && deliveryOption.sameDayPickUp.isChecked);
            const { router = {}, location = {} } = action.payload;
            const queryObj = makeQuery(location.search);
            if (isBopisChecked && queryObj && queryObj.storeIds && router.replace) {
                const query = {
                    ...queryObj,
                    storeIds: '',
                };
                /* router.replace is for removing store id's from the url to avoid gallery story display when landing from cart page */
                router.replace({
                    pathname: location.pathname,
                    query,
                });
            }
            yield put({
                type: ADDTOCART_POST_SUCCESS,
                cartSuccess: {
                    ...cartSuccess,
                    isStoreModalAtc,
                },
            });
            const isBopisATC = !!(
                isBopisChecked &&
                sdpuStoreInfo.storeId &&
                sdpuStoreInfo.storeName
            );
            const fetchStoreResponse = yield call(fetchStores, sdpuStoreInfo.storeId);
            const curbside = get(fetchStoreResponse, 'data.stores[0].curbside', null);
            sdpuStoreInfo.curbsidePickupIndicator = !isEmpty(curbside);
            if (isBopisATC) {
                yield all([put(getSdpuStoreData(sdpuStoreInfo)), put(setSdpuLocalStorage())]);
            }
        } else if (cartSuccess.response && cartSuccess.response.status !== 401) {
            const { errorCode, errorMessage = defaultErrorMessage } = getErrorMessage(
                cartSuccess.response
            );
            if (
                cartSuccess.response.status !== 412 ||
                errorCode !== 'INITIATE_CHECKOUT_AGAIN' ||
                apiCounter === 2
            ) {
                yield put(loadingActions.removeLoader());
                yield put(
                    messagesActions.addMessage({
                        section: ADD_TO_CART_MSG_ID,
                        message: {
                            id: errorCode,
                            title: errorMessage,
                        },
                        sourceMiniPDP: false,
                    })
                );
                yield put(formErrorAnalytics([{ errorDescription: errorMessage.toLowerCase() }]));
            }
        }
    } catch (error) {
        yield put(
            messagesActions.removeAllMessages({
                section: ADD_TO_CART_MSG_ID,
                sourceMiniPDP: false,
            })
        );
        const GenericApiErrorMessages = yield select(getGenericApiErrorMessages);
        yield put(loadingActions.removeLoader());
        yield put({ type: ADDTOCART_POST_ERROR, error });
        const errorTitle = error.errorMessage || GenericApiErrorMessages.apiErrorMessage;
        yield put(
            messagesActions.addMessage({
                section: ADD_TO_CART_MSG_ID,
                message: {
                    id: error.errorCode,
                    title: errorTitle,
                },
                sourceMiniPDP: false,
            })
        );
        yield put(formErrorAnalytics([{ errorDescription: errorTitle.toLowerCase() }]));
    }
}

const watchAddToCartSaga = function* watchAddToCartSaga() {
    yield takeLatest(ADDTOCART_POST_REQUEST, AddToCartSaga);
};

watchAddToCartSaga.sagaName = 'addToCartSaga';

export default watchAddToCartSaga;
